import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { ArrowLeft, Images, Trash2 } from 'lucide-react'
import { useCreateMember, useUpdateMember, useMember } from '@/hooks/use-members'
import type { Tables } from '@/types/supabase'
import MediaPicker from '@/components/media/media-picker'

type IdolMember = Tables<'idol_members'>

type Tab = 'profil' | 'personal' | 'sosial' | 'pengaturan'

interface MemberSocialLinks {
  instagram?: string
  twitter?: string
  youtube?: string
  tiktok?: string
}

type BirthdayVisibility = 'full' | 'month_day' | 'hidden'

interface FormState {
  name: string
  stage_name: string
  position: string
  bio: string
  display_order: number
  is_active: boolean
  birthday: string
  joined_at: string
  still_active: boolean
  left_at: string
  personal_color: string
  jikoushokai: string
  generation: string
  element: string
  social_links: MemberSocialLinks
  birthday_visibility: BirthdayVisibility
}

const EMPTY_FORM: FormState = {
  name: '',
  stage_name: '',
  position: '',
  bio: '',
  display_order: 0,
  is_active: true,
  birthday: '',
  joined_at: '',
  still_active: true,
  left_at: '',
  personal_color: '#ffffff',
  jikoushokai: '',
  generation: '',
  element: '',
  social_links: {},
  birthday_visibility: 'month_day',
}

const SOCIAL_FIELDS: { key: keyof MemberSocialLinks; label: string; prefix: string; placeholder: string }[] = [
  { key: 'instagram', label: 'Instagram',   prefix: 'instagram.com/',  placeholder: 'username' },
  { key: 'twitter',   label: 'Twitter / X', prefix: 'x.com/',          placeholder: 'username' },
  { key: 'youtube',   label: 'YouTube',     prefix: 'youtube.com/@',   placeholder: 'handle'   },
  { key: 'tiktok',    label: 'TikTok',      prefix: 'tiktok.com/@',    placeholder: 'username' },
]

export default function MemberFormPage() {
  const { id } = useParams()
  const navigate = useNavigate()
  const isEdit = !!id

  const { data: member, isLoading } = useMember(id)
  const createMember = useCreateMember()
  const updateMember = useUpdateMember()

  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [photoUrl, setPhotoUrl] = useState<string | null>(null)
  const [photoAssetId, setPhotoAssetId] = useState<string | null>(null)
  const [mediaPickerOpen, setMediaPickerOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<Tab>('profil')

  useEffect(() => {
    if (member) {
      const m = member as unknown as IdolMember & {
        birthday?: string | null
        joined_at?: string | null
        left_at?: string | null
        personal_color?: string | null
        jikoushokai?: string | null
        generation?: string | null
        element?: string | null
        social_links?: MemberSocialLinks | null
        birthday_visibility?: string | null
      }
      setForm({
        name: m.name,
        stage_name: m.stage_name ?? '',
        position: m.position ?? '',
        bio: m.bio ?? '',
        display_order: m.display_order,
        is_active: m.is_active,
        birthday: m.birthday ?? '',
        joined_at: m.joined_at ?? '',
        still_active: !m.left_at,
        left_at: m.left_at ?? '',
        personal_color: m.personal_color ?? '#ffffff',
        jikoushokai: m.jikoushokai ?? '',
        generation: m.generation ?? '',
        element: m.element ?? '',
        social_links: (m.social_links as MemberSocialLinks) ?? {},
        birthday_visibility: (m.birthday_visibility as BirthdayVisibility) ?? 'month_day',
      })
      setPhotoUrl(m.photo_url ?? null)
      setPhotoAssetId((m as unknown as { asset_id?: string | null }).asset_id ?? null)
    }
  }, [member?.id])

  function set<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }))
  }

  function handleMediaSelect(assetId: string, url: string) {
    setPhotoAssetId(assetId)
    setPhotoUrl(url)
    setMediaPickerOpen(false)
  }

  const isPending = createMember.isPending || updateMember.isPending
  const mutationError = createMember.error ?? updateMember.error

  async function handleSave() {
    const payload = {
      name: form.name.trim(),
      stage_name: form.stage_name.trim() || null,
      position: form.position.trim() || null,
      bio: form.bio.trim() || null,
      display_order: form.display_order,
      is_active: form.is_active,
      photo_url: photoUrl,
      asset_id: photoAssetId,
      birthday: form.birthday || null,
      joined_at: form.joined_at || null,
      left_at: form.still_active ? null : (form.left_at || null),
      personal_color: form.personal_color || null,
      jikoushokai: form.jikoushokai.trim() || null,
      generation: form.generation.trim() || null,
      element: form.element.trim() || null,
      social_links: Object.keys(form.social_links).length > 0 ? form.social_links : null,
      birthday_visibility: form.birthday_visibility,
    }

    if (isEdit && id) {
      updateMember.mutate(
        { ...(payload as Parameters<typeof updateMember.mutate>[0]), id },
        { onSuccess: () => navigate('/members') },
      )
    } else {
      createMember.mutate(
        payload as Parameters<typeof createMember.mutate>[0],
        { onSuccess: () => navigate('/members') },
      )
    }
  }

  if (isEdit && isLoading) {
    return <p className="text-muted-foreground">Memuat data member...</p>
  }

  const displayName = form.stage_name.trim() || form.name.trim() || '?'
  const initial = displayName.charAt(0).toUpperCase()

  return (
    <>
      <div className="max-w-2xl space-y-6">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => navigate('/members')}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold">{isEdit ? 'Edit Member' : 'Tambah Member'}</h1>
            <p className="text-muted-foreground text-sm">
              {isEdit ? 'Perbarui data member.' : 'Isi informasi member baru.'}
            </p>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-border">
          {([
            { key: 'profil',      label: 'Profil' },
            { key: 'personal',    label: 'Personal' },
            { key: 'sosial',      label: 'Media Sosial' },
            { key: 'pengaturan',  label: 'Pengaturan' },
          ] as { key: Tab; label: string }[]).map(({ key, label }) => (
            <button
              key={key}
              type="button"
              onClick={() => setActiveTab(key)}
              className={`px-5 py-2.5 text-sm font-medium transition-colors border-b-2 -mb-px ${
                activeTab === key
                  ? 'border-primary text-foreground'
                  : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        {/* Tab: Profil */}
        {activeTab === 'profil' && (
          <div className="space-y-6">
            {/* Foto */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Foto</h2>
              <div className="flex items-center gap-4">
                <div className="shrink-0 h-20 w-20 rounded-full overflow-hidden bg-muted flex items-center justify-center text-2xl font-semibold text-muted-foreground">
                  {photoUrl ? (
                    <img src={photoUrl} alt="preview" className="h-full w-full object-cover" />
                  ) : (
                    <span>{initial}</span>
                  )}
                </div>
                <div className="space-y-1.5">
                  <button
                    type="button"
                    onClick={() => setMediaPickerOpen(true)}
                    className="inline-flex items-center gap-2 rounded-md border border-border px-3 py-1.5 text-sm hover:bg-accent transition-colors"
                  >
                    <Images className="h-3.5 w-3.5" />
                    {photoUrl ? 'Ganti Foto' : 'Pilih dari Media'}
                  </button>
                  {photoUrl && (
                    <button
                      type="button"
                      onClick={() => { setPhotoUrl(null); setPhotoAssetId(null) }}
                      className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-destructive transition-colors"
                    >
                      <Trash2 className="h-3 w-3" />
                      Hapus foto
                    </button>
                  )}
                  <p className="text-xs text-muted-foreground">Pilih dari Media Library atau upload foto baru.</p>
                </div>
              </div>
            </div>

            {/* Identitas */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Identitas</h2>
              <div className="space-y-1">
                <label className="text-sm font-medium">Nama *</label>
                <input
                  required
                  value={form.name}
                  onChange={(e) => set('name', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="Nama asli member"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Stage Name</label>
                  <input
                    value={form.stage_name}
                    onChange={(e) => set('stage_name', e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Nama panggung (opsional)"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Posisi</label>
                  <input
                    value={form.position}
                    onChange={(e) => set('position', e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    placeholder="Vocal, Dance, Leader…"
                  />
                </div>
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Bio</label>
                <textarea
                  rows={4}
                  value={form.bio}
                  onChange={(e) => set('bio', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Deskripsi singkat member (opsional)"
                />
              </div>
            </div>
          </div>
        )}

        {/* Tab: Personal */}
        {activeTab === 'personal' && (
          <div className="space-y-6">
            {/* Info Dasar */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Info Dasar</h2>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <label className="text-sm font-medium">Tanggal Lahir</label>
                  <input
                    type="date"
                    value={form.birthday}
                    onChange={(e) => set('birthday', e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-sm font-medium">Bergabung Sejak</label>
                  <input
                    type="date"
                    value={form.joined_at}
                    onChange={(e) => set('joined_at', e.target.value)}
                    className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer select-none">
                  <input
                    type="checkbox"
                    checked={form.still_active}
                    onChange={(e) => set('still_active', e.target.checked)}
                    className="rounded border-input accent-primary"
                  />
                  <span className="text-sm">Masih aktif di grup</span>
                </label>
                {!form.still_active && (
                  <div className="space-y-1 ml-6">
                    <label className="text-sm text-muted-foreground">Tanggal keluar</label>
                    <input
                      type="date"
                      value={form.left_at}
                      onChange={(e) => set('left_at', e.target.value)}
                      className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                    />
                  </div>
                )}
              </div>
              <div className="space-y-1">
                <label className="text-sm font-medium">Generasi</label>
                <input
                  value={form.generation}
                  onChange={(e) => set('generation', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="contoh: Gen 1, Generasi Pertama"
                />
              </div>
            </div>

            {/* Personal Color */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div>
                <h2 className="font-semibold">Personal Color</h2>
                <p className="text-sm text-muted-foreground">Warna ikonik member yang ditampilkan di halaman publik.</p>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="color"
                  value={form.personal_color}
                  onChange={(e) => set('personal_color', e.target.value)}
                  className="h-8 w-10 cursor-pointer rounded border border-input"
                />
                <input
                  type="text"
                  value={form.personal_color}
                  onChange={(e) => set('personal_color', e.target.value)}
                  className="w-32 rounded-md border border-input bg-background px-3 py-1.5 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="#ffffff"
                  maxLength={7}
                />
                <div
                  className="h-8 w-8 rounded-md border border-border"
                  style={{ backgroundColor: form.personal_color }}
                />
              </div>
            </div>

            {/* Atribut */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <h2 className="font-semibold">Atribut / Elemen</h2>
              <div className="space-y-1">
                <label className="text-sm font-medium">Elemen atau atribut member</label>
                <input
                  value={form.element}
                  onChange={(e) => set('element', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
                  placeholder="contoh: Fire, Water, Wind, Earth"
                />
              </div>
            </div>

            {/* Jikoushokai */}
            <div className="rounded-xl border border-border bg-card p-6 space-y-4">
              <div>
                <h2 className="font-semibold">Jikoushokai</h2>
                <p className="text-sm text-muted-foreground">Teks perkenalan diri (自己紹介) member.</p>
              </div>
              <div className="space-y-1">
                <textarea
                  rows={5}
                  value={form.jikoushokai}
                  onChange={(e) => set('jikoushokai', e.target.value)}
                  className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none"
                  placeholder="Teks perkenalan member di atas panggung..."
                />
                <p className="text-xs text-muted-foreground">{form.jikoushokai.length} karakter</p>
              </div>
            </div>
          </div>
        )}

        {/* Tab: Media Sosial */}
        {activeTab === 'sosial' && (
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <h2 className="font-semibold">Media Sosial Member</h2>
            <p className="text-sm text-muted-foreground">Masukkan username saja, bukan URL lengkap.</p>
            <div className="space-y-3">
              {SOCIAL_FIELDS.map(({ key, label, prefix, placeholder }) => (
                <div key={key} className="space-y-1">
                  <label className="text-sm font-medium">{label}</label>
                  <div className="flex items-center rounded-md border border-input bg-background overflow-hidden focus-within:ring-2 focus-within:ring-ring">
                    <span className="shrink-0 px-3 py-2 text-sm text-muted-foreground bg-muted/50 border-r border-input select-none">
                      {prefix}
                    </span>
                    <input
                      type="text"
                      value={form.social_links[key] ?? ''}
                      onChange={(e) =>
                        set('social_links', {
                          ...form.social_links,
                          [key]: e.target.value.replace(/^@/, '').trim() || undefined,
                        })
                      }
                      className="flex-1 bg-transparent px-3 py-2 text-sm focus:outline-none"
                      placeholder={placeholder}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tab: Pengaturan */}
        {activeTab === 'pengaturan' && (
          <div className="rounded-xl border border-border bg-card p-6 space-y-4">
            <h2 className="font-semibold">Pengaturan Tampilan</h2>
            <div className="space-y-1 w-36">
              <label className="text-sm font-medium">Urutan tampil</label>
              <input
                type="number"
                min={0}
                value={form.display_order}
                onChange={(e) => set('display_order', Number(e.target.value))}
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <p className="text-xs text-muted-foreground">Angka lebih kecil tampil lebih dulu.</p>
            </div>
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
                className="rounded border-input accent-primary"
              />
              <span className="text-sm font-medium">Tampilkan di halaman publik</span>
            </label>
            <div className="space-y-2 pt-2 border-t border-border">
              <label className="text-sm font-medium">Tampilan tanggal lahir di halaman publik</label>
              <div className="space-y-1.5">
                {([
                  { value: 'full',      label: 'Tanggal lengkap',       desc: 'Contoh: 14 Februari 2000' },
                  { value: 'month_day', label: 'Tanggal & bulan saja',  desc: 'Contoh: 14 Februari' },
                  { value: 'hidden',    label: 'Sembunyikan',           desc: 'Tidak ditampilkan' },
                ] as { value: BirthdayVisibility; label: string; desc: string }[]).map(({ value, label, desc }) => (
                  <label key={value} className="flex items-start gap-2.5 cursor-pointer select-none">
                    <input
                      type="radio"
                      name="birthday_visibility"
                      value={value}
                      checked={form.birthday_visibility === value}
                      onChange={() => set('birthday_visibility', value)}
                      className="mt-0.5 accent-primary"
                    />
                    <span className="flex flex-col">
                      <span className="text-sm font-medium leading-none">{label}</span>
                      <span className="text-xs text-muted-foreground mt-0.5">{desc}</span>
                    </span>
                  </label>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Save */}
        <div className="flex items-center gap-3">
          <button
            onClick={handleSave}
            disabled={isPending || !form.name.trim()}
            className="rounded-md bg-primary px-6 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {isPending ? 'Menyimpan...' : isEdit ? 'Simpan Perubahan' : 'Tambah Member'}
          </button>
          <button
            type="button"
            onClick={() => navigate('/members')}
            className="rounded-md border border-border px-4 py-2 text-sm hover:bg-accent"
          >
            Batal
          </button>
          {mutationError && (
            <p className="text-sm text-destructive">
              {(mutationError as Error).message ?? 'Terjadi kesalahan.'}
            </p>
          )}
        </div>
      </div>

      <MediaPicker
        open={mediaPickerOpen}
        onClose={() => setMediaPickerOpen(false)}
        onSelect={handleMediaSelect}
      />
    </>
  )
}
